import React from "react";
import useDataManagerColumnPreferences from "../../hooks/useDataManagerColumnPreferences.js";
import useSortableColumnHeaderDrag from "../../hooks/useSortableColumnHeaderDrag.js";
import { fetchColumnPreferences, saveColumnPreferences } from "../../lib/dataManagerApi.js";
import ColumnHeaderDragOverlay from "../ColumnHeaderDragOverlay.jsx";
import { useToast } from "../ResultsPrimitives.jsx";

const TABLE_KEY = "billing_consolidation_visits";
const ColumnLayout = React.createContext(null);
export const VisitColumnLayout = ColumnLayout.Provider;

// Les nouvelles colonnes rejoignent leur voisin dans les ordres déjà enregistrés.
function placeNewColumns(preferences) {
  let order = preferences?.column_order;
  if (!Array.isArray(order)) return preferences;
  if (order.includes("product_code") && !order.includes("silo")) order = order.flatMap(key => key === "product_code" ? [key, "silo"] : [key]);
  if (order.includes("stop") && !order.includes("loading_reference")) order = order.flatMap(key => key === "stop" ? [key, "loading_reference"] : [key]);
  return { ...preferences, column_order: order };
}

export function useVisitTableColumns(originalColumns) {
  const { showToast } = useToast();
  const [savedPrefs, setSavedPrefs] = React.useState(null);
  const [loaded, setLoaded] = React.useState(false);
  React.useEffect(() => {
    let disposed = false;
    fetchColumnPreferences(TABLE_KEY).then(prefs => {
      if (!disposed) { setSavedPrefs(placeNewColumns(prefs)); setLoaded(true); }
    }).catch(() => {
      if (!disposed) {
        setLoaded(true);
        showToast({ type: "error", title: "Colonnes", message: "Impossible de charger vos réglages de colonnes." });
      }
    });
    return () => { disposed = true; };
  }, []);
  const onSaveError = React.useCallback(error => {
    showToast({ type: "error", title: "Colonnes non enregistrées", message: `${error.message} Les derniers réglages enregistrés ont été restaurés.` });
  }, [showToast]);
  // Les colonnes masquées gardent aussi leur place et leur largeur enregistrées.
  const stopWidth = originalColumns[0].defaultWidth;
  const scale = typeof document === "undefined" ? 1 : (parseFloat(getComputedStyle(document.documentElement).fontSize) || 16) / 16;
  const baseColumns = React.useMemo(() => [
    { key: "stop", defaultWidth: stopWidth },
    { key: "loading_reference", defaultWidth: 128 },
    { key: "work_order_number", defaultWidth: 88 },
    { key: "product", defaultWidth: 112 },
    { key: "product_code", defaultWidth: 72 },
    { key: "silo", defaultWidth: 64 },
    // « COMPTOIR AGRICOLE… », avec les marges et la flèche du sélecteur.
    { key: "coop", defaultWidth: 180 },
    { key: "planned_tons", defaultWidth: 64 },
    { key: "actual_tons", defaultWidth: 72 },
    { key: "spacer", defaultWidth: 0 },
    { key: "planned_arrival", defaultWidth: 72 },
    { key: "arrival", defaultWidth: 112 },
    { key: "planned_departure", defaultWidth: 72 },
    { key: "departure", defaultWidth: 112 },
  ].map(column => ({ ...column, defaultWidth: Math.round(column.defaultWidth * scale), minWidth: column.key === "spacer" ? 1 : 50 })), [stopWidth, scale]);
  const prefs = useDataManagerColumnPreferences({ baseColumns, savedPrefs, tableKey: TABLE_KEY, saveColumnPreferences, onSaveError, normalizeIncoming: placeNewColumns });
  const visible = new Map(originalColumns.map(column => [column.key, column]));
  const columns = prefs.resolvedColumns.filter(column => visible.has(column.key))
    .map(column => ({ ...visible.get(column.key), ...column, width: Math.max(column.width, column.minWidth) }));
  return { originalColumns, columns, prefs, loaded, width: columns.reduce((sum, column) => sum + column.width, 0) };
}

// Une cellule fusionnée peut être coupée par une colonne déplacée : son contenu
// reste attaché à sa première colonne d'origine et n'est jamais dupliqué.
export function arrangeVisitCells(children, originalKeys, orderedKeys) {
  const cells = [];
  const flatten = items => React.Children.forEach(items, child => {
    if (!React.isValidElement(child)) return;
    if (child.type === React.Fragment) flatten(child.props.children);
    else cells.push(child);
  });
  flatten(children);
  let offset = 0;
  const slots = new Map();
  cells.forEach((cell, index) => {
    const span = cell.props.colSpan || 1;
    for (let part = 0; part < span; part++) {
      slots.set(originalKeys[offset + part], { cell, index, owner: part === 0 });
    }
    offset += span;
  });
  const groups = [];
  orderedKeys.forEach(key => {
    const slot = slots.get(key);
    if (!slot) return;
    const previous = groups.at(-1);
    if (previous?.index === slot.index && !slot.owner) {
      previous.span++;
      previous.owner ||= slot.owner;
    } else groups.push({ ...slot, key, span: 1 });
  });
  return groups.map(group => React.cloneElement(group.cell, {
    key: group.owner ? `cell:${group.index}` : `blank:${group.index}:${group.key}`,
    colSpan: group.span,
    children: group.owner ? group.cell.props.children : null,
  }));
}

export function VisitTableRow({ children, ...props }) {
  const layout = React.useContext(ColumnLayout);
  return <tr {...props}>{layout ? arrangeVisitCells(children,
    layout.originalColumns.map(column => column.key), layout.columns.map(column => column.key)) : children}</tr>;
}

export function VisitColumnHeader({ column, layout, children }) {
  const thRef = React.useRef(null);
  const spacer = column.key === "spacer";
  const { dragOverlay, isDragging } = useSortableColumnHeaderDrag({
    columnKey: column.key, columnKeyAttribute: "data-visit-column-key",
    draggable: !spacer && layout.loaded, label: column.label, onMove: layout.prefs.handleColumnMove, thRef,
  });
  const resize = event => {
    if (!["ArrowLeft", "ArrowRight"].includes(event.key)) return;
    event.preventDefault(); event.stopPropagation();
    layout.prefs.handleResizeEnd(column.key, column.width + (event.key === "ArrowRight" ? 1 : -1) * (event.shiftKey ? 50 : 10));
  };
  // Nom de l'en-tête = son libellé seul : sans cela, le bouton de redimensionnement s'ajoute au nom annoncé pour chaque cellule.
  return <th ref={thRef} scope="col" data-visit-column-key={spacer ? undefined : column.key} aria-label={spacer ? undefined : column.label}
    aria-hidden={spacer || undefined} tabIndex={spacer || !layout.loaded ? undefined : 0}
    title={spacer ? undefined : `${column.label} · Glisser pour déplacer ; Alt + flèche gauche ou droite au clavier.`}
    onKeyDown={event => {
      if (!event.altKey || !["ArrowLeft", "ArrowRight"].includes(event.key)) return;
      const direction = event.key === "ArrowLeft" ? -1 : 1;
      const visible = layout.columns.filter(item => item.key !== "spacer");
      const target = visible[visible.findIndex(item => item.key === column.key) + direction];
      if (target) { event.preventDefault(); layout.prefs.handleColumnMove(column.key, target.key, direction < 0 ? "before" : "after"); }
    }}
    className={`relative ${["planned_tons", "actual_tons"].includes(column.key) ? "px-1 whitespace-nowrap" : "px-2"} py-1.5 !text-[11px] font-semibold !normal-case !tracking-normal text-center leading-tight focus-visible:outline-accent ${spacer ? "!p-0" : "cursor-grab touch-none select-none"} ${column.key === "stop" ? "pl-9 text-left" : ""} ${isDragging ? "opacity-30" : ""}`}>
    {children || column.label}
    {!spacer ? <button type="button" disabled={!layout.loaded} aria-label={`Redimensionner la colonne ${column.label}`}
      title="Glisser pour redimensionner ; flèches gauche et droite au clavier. Double-clic pour rétablir la largeur."
      className="absolute inset-y-0 right-0 z-10 w-2 cursor-col-resize touch-none select-none rounded-[4px] bg-transparent p-0 after:absolute after:bottom-1 after:left-1/2 after:top-1 after:w-px after:bg-slate-300 hover:after:bg-accent focus-visible:outline-2 focus-visible:outline-accent"
      onPointerDown={event => layout.prefs.handleResizePointerDown(event, column.key)}
      onKeyDown={resize} onClick={event => event.stopPropagation()}
      onDoubleClick={() => layout.prefs.handleResizeEnd(column.key, column.defaultWidth)} /> : null}
    <ColumnHeaderDragOverlay dragOverlay={dragOverlay} />
  </th>;
}
